import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './LeadStats.css';

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  proposal_sent: 'Proposal Sent', converted: 'Converted', lost: 'Lost'
};

function LeadStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }

    (async () => {
      try {
        setStats(await apiClient.get('/api/leads/stats'));
      } catch (err) {
        console.error('Error fetching lead stats:', err);
        setError('Failed to load stats');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) return <PageShell><p>Loading stats...</p></PageShell>;
  if (error || !stats) return <PageShell><div className="lead-stats-error">{error || 'No data available.'}</div></PageShell>;

  const cards = [
    { key: 'active', label: 'Active Leads', value: stats.totalActive, icon: 'fas fa-bullseye' },
    { key: 'conversion', label: 'Conversion Rate', value: stats.conversionRate !== null ? `${stats.conversionRate.toFixed(1)}%` : '—', icon: 'fas fa-chart-line' },
    { key: 'avgvalue', label: 'Avg. Estimated Value', value: stats.averageValue !== null ? `£${stats.averageValue.toFixed(2)}` : '—', icon: 'fas fa-sterling-sign' },
    { key: 'converted30', label: 'Converted Last 30 Days', value: stats.convertedLast30Days, icon: 'fas fa-handshake' }
  ];

  const maxFunnel = Math.max(1, ...Object.values(stats.funnel));
  const maxSource = Math.max(1, ...stats.bySource.map((s) => s.count));

  return (
    <PageShell>
      <div className="lead-stats-page">
        <h1>Lead Stats</h1>
        <p className="lead-stats-intro">Pipeline health, conversion, and where leads are actually coming from.</p>

        <div className="lead-stats-grid">
          {cards.map((c) => (
            <div key={c.key} className="lead-stats-card">
              <div className="lead-stats-card-icon"><i className={c.icon}></i></div>
              <div>
                <div className="lead-stats-card-value">{c.value}</div>
                <div className="lead-stats-card-label">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="lead-stats-panels">
          <div className="lead-stats-panel">
            <h2>Pipeline by Stage</h2>
            <div className="lead-stats-bars">
              {Object.entries(stats.funnel).map(([status, count]) => (
                <div key={status} className="lead-stats-bar-row">
                  <span className="lead-stats-bar-label">{STATUS_LABELS[status] || status}</span>
                  <div className="lead-stats-bar-track">
                    <div className="lead-stats-bar" style={{ width: `${(count / maxFunnel) * 100}%` }}></div>
                  </div>
                  <span className="lead-stats-bar-count">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lead-stats-panel">
            <h2>Leads by Source</h2>
            {stats.bySource.length === 0 ? (
              <p className="lead-stats-empty">No leads yet.</p>
            ) : (
              <div className="lead-stats-bars">
                {stats.bySource.map((s) => (
                  <div key={s.source} className="lead-stats-bar-row">
                    <span className="lead-stats-bar-label">{s.source}</span>
                    <div className="lead-stats-bar-track">
                      <div className="lead-stats-bar lead-stats-bar-alt" style={{ width: `${(s.count / maxSource) * 100}%` }}></div>
                    </div>
                    <span className="lead-stats-bar-count">{s.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lead-stats-panel">
            <h2>Converted in the Last 30 Days, By Rep</h2>
            {stats.leaderboard.length === 0 ? (
              <p className="lead-stats-empty">Nothing converted in the last 30 days yet.</p>
            ) : (
              <ul className="lead-stats-leaderboard">
                {stats.leaderboard.map((row) => (
                  <li key={row.name}>
                    <span>{row.name}</span>
                    <span className="lead-stats-leaderboard-count">{row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

export default LeadStats;
